import type {Context} from '../types';

/**
 * This patch stringifies certain node.data id properties that were sometimes accidentally
 * persisted as bson mongoIds when cloning a site.
 *
 * "data.img.id"
 * "data.dyn.collection.id"
 * "data.dyn.collection.contextCollectionId"
 * "data.link.assetId"
 * "data.link.pageId"
 * "data.link.collectionId"
 * "data.link.itemId"
 */

const needsStringification = (idVal: unknown): boolean => {
  return Boolean(
    idVal && typeof idVal === 'object' && typeof idVal.toString === 'function'
  );
};

export default (context: Context) => {
  const {
    node: {jsonNode},
  } = context;
  const {data = {}} = jsonNode;
  // data shortcuts
  const dynCollectionData = data.dyn && data.dyn.collection;
  const linkData = data.link;

  // ids to check
  const imgId = data.img && data.img.id;
  const dynCollectionId = dynCollectionData && dynCollectionData.id;
  const dynContextCollectionId =
    dynCollectionData && dynCollectionData.contextCollectionId;
  const linkAssetId = linkData && linkData.assetId;
  const linkPageId = linkData && linkData.pageId;
  const linkCollectionId = linkData && linkData.collectionId;
  const linkItemId = linkData && linkData.itemId;

  // are they valid?
  const invalid_imgId = needsStringification(imgId);
  const invalid_dynCollectionId = needsStringification(dynCollectionId);
  const invalid_dynContextCollectionId = needsStringification(
    dynContextCollectionId
  );
  const invalid_linkAssetId = needsStringification(linkAssetId);
  const invalid_linkPageId = needsStringification(linkPageId);
  const invalid_linkCollectionId = needsStringification(linkCollectionId);
  const invalid_linkItemId = needsStringification(linkItemId);

  const needsMigration =
    invalid_imgId ||
    invalid_dynCollectionId ||
    invalid_dynContextCollectionId ||
    invalid_linkAssetId ||
    invalid_linkPageId ||
    invalid_linkCollectionId ||
    invalid_linkItemId;

  return {
    needed: needsMigration,
    logData: {
      msg: 'Node with non-string ids',
      meta: {
        nodeId: jsonNode._id,
      },
    },
    apply: () => {
      if (invalid_imgId) {
        data.img.id = String(imgId);
      }

      if (invalid_dynCollectionId) {
        data.dyn.collection.id = String(dynCollectionId);
      }

      if (invalid_dynContextCollectionId) {
        data.dyn.collection.contextCollectionId = String(
          dynContextCollectionId
        );
      }

      if (invalid_linkAssetId) {
        data.link.assetId = String(linkAssetId);
      }

      if (invalid_linkPageId) {
        data.link.pageId = String(linkPageId);
      }

      if (invalid_linkCollectionId) {
        data.link.collectionId = String(linkCollectionId);
      }

      if (invalid_linkItemId) {
        data.link.itemId = String(linkItemId);
      }
    },
  };
};
